import { Vector3 } from "three";

// The Artemis II replay: Orion's path (public/missions/artemis-ii.json, built
// by scripts/build-artemis-ii.js from JPL Horizons), placed in the globe's
// scene, and paced so the mission's nine days play in about a minute.

export const ARTEMIS_II_URL = "/missions/artemis-ii.json";
// The replay's opening flight to the pad; playback starts after it.
export const MISSION_INTRO_MS = 2000;
export const ARTEMIS_II_LAUNCH = Date.parse("2026-04-01T22:35:12Z");
const R_EARTH = 6371;
const R_MOON = 1737.4;

// Where Orion's position hands over from Earth-centred to Moon-centred
// placement (distance from the Moon's centre, km); see createMission.
const MOON_FRAME_FROM = 90_000;
const MOON_FRAME_TO = 30_000;

// The launch-day story: a dot at pad 39B while the timeline's window covers
// the launch (it ends between liftoff and a day and a half after).
export const ARTEMIS_II_STORY = {
  article_id: "special:artemis-ii",
  special: "artemis-ii",
  lat: 28.6272,
  lng: -80.6208,
  val: 10,
  importance: 10,
  title: "Artemis II launches: four astronauts head for the Moon",
  description:
    "NASA's Space Launch System lifted off from Kennedy Space Center at 6:35 pm EDT carrying Orion and its crew, " +
    "Reid Wiseman, Victor Glover, Christina Koch and Jeremy Hansen, on the first crewed flight around the Moon since 1972. " +
    "Over nine days they'll loop around the far side and return to a Pacific splashdown.",
  image: null,
  date: "2026-04-01T22:35:12Z",
  publishedAt: ARTEMIS_II_LAUNCH,
  link: "https://www.nasa.gov/mission/artemis-ii/",
  source: "www.nasa.gov",
  country: "United States",
  location: "Kennedy Space Center, Florida",
  locationPrecision: "place",
  category: "Science & Tech",
};
const STORY_WINDOW_MS = 36 * 3_600_000;

/** The special stories to add to a window ending at `windowEndMs`. */
export function specialStoriesFor(windowEndMs) {
  const age = windowEndMs - ARTEMIS_II_LAUNCH;
  return age >= 0 && age <= STORY_WINDOW_MS ? [ARTEMIS_II_STORY] : [];
}

const smoothstep = (e0, e1, x) => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

// Same GMST as sun.js, so Orion's Earth-fixed position agrees with the sky.
const gmstRad = (ms) => ((280.46061837 + 360.98564736629 * (ms / 86_400_000 - 10957.5)) * Math.PI) / 180;

// The globe's scene is Earth-fixed (Globe.js polar2Cartesian: x, y, z =
// Earth-fixed y, z, x). The mission is laid out in "inertial scene"
// coordinates, the same mapping applied to ICRF axes; turning them about the
// scene's polar (y) axis by -GMST gives the Earth-fixed scene. Drawing the
// path and placing the camera inertially keeps the free-return figure-8 still
// while the Earth turns under it, as a sped-up mission replay should look
// (Earth-fixed, deep space would whirl round the globe once a day).
const sceneOf = (x, y, z, out = new Vector3()) => out.set(y, z, x);
/** The angle (about scene y) from inertial to Earth-fixed scene at `ms`. */
export const frameAngleAt = (ms) => -gmstRad(ms);
const Y_AXIS = new Vector3(0, 1, 0);

// Monotone cubic interpolation (Fritsch–Carlson) through knots (x, y).
export function monotoneCubic(xs, ys) {
  const n = xs.length;
  const d = xs.slice(1).map((x, i) => (ys[i + 1] - ys[i]) / (x - xs[i]));
  const m = xs.map((_, i) => (i === 0 ? d[0] : i === n - 1 ? d[n - 2] : d[i - 1] * d[i] <= 0 ? 0 : (d[i - 1] + d[i]) / 2));
  for (let i = 0; i < n - 1; i++) {
    if (d[i] === 0) { m[i] = m[i + 1] = 0; continue; }
    const a = m[i] / d[i], b = m[i + 1] / d[i];
    const h = Math.hypot(a, b);
    if (h > 3) { m[i] = (3 * a * d[i]) / h; m[i + 1] = (3 * b * d[i]) / h; }
  }
  return (x) => {
    if (x <= xs[0]) return ys[0];
    if (x >= xs[n - 1]) return ys[n - 1];
    let i = 0;
    while (x > xs[i + 1]) i++;
    const h = xs[i + 1] - xs[i];
    const t = (x - xs[i]) / h;
    return (2 * t ** 3 - 3 * t ** 2 + 1) * ys[i] + (t ** 3 - 2 * t ** 2 + t) * h * m[i] +
      (-2 * t ** 3 + 3 * t ** 2) * ys[i + 1] + (t ** 3 - t ** 2) * h * m[i + 1];
  };
}

/**
 * The mission in the globe's scene. `scene` gives the globe's radius and
 * where (and how big) the Moon is drawn: about five times nearer than the
 * real one, at its real angular size. Orion's distance from Earth is
 * compressed to match (radius ∝ r^γ, with γ putting the Moon's distance at
 * the drawn Moon's), and near the Moon its position is taken relative to the
 * drawn Moon at the drawn Moon's scale, so the flyby passes it as it did.
 */
export function createMission(data, { globeRadius = 100, moonDistance = 1200, moonRadius = 5.4 } = {}) {
  const launchMs = Date.parse(data.launch);
  const track = data.track;
  const times = track.map((p) => p[0]);
  const duration = times[times.length - 1];
  const milestones = data.milestones.map((m) => ({ ...m, ms: launchMs + m.t * 1000 }));
  const at = (id) => data.milestones.find((m) => m.id === id).t;

  const segment = (t) => {
    let lo = 0, hi = times.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (times[mid] <= t) lo = mid; else hi = mid;
    }
    return lo;
  };

  // Orion's ICRF position and velocity at t seconds after launch (Hermite).
  const orion = (t) => {
    const i = segment(t);
    const a = track[i], b = track[i + 1];
    const h = b[0] - a[0];
    const s = (t - a[0]) / h;
    const [h00, h10, h01, h11] = [2 * s ** 3 - 3 * s ** 2 + 1, s ** 3 - 2 * s ** 2 + s, -2 * s ** 3 + 3 * s ** 2, s ** 3 - s ** 2];
    const [d00, d10, d01, d11] = [6 * s ** 2 - 6 * s, 3 * s ** 2 - 4 * s + 1, -6 * s ** 2 + 6 * s, 3 * s ** 2 - 2 * s];
    const r = [0, 1, 2].map((k) => h00 * a[1 + k] + h10 * h * a[4 + k] + h01 * b[1 + k] + h11 * h * b[4 + k]);
    const v = [0, 1, 2].map((k) => (d00 * a[1 + k] + d01 * b[1 + k]) / h + d10 * a[4 + k] + d11 * b[4 + k]);
    return { r, v };
  };

  // The Moon's ICRF position (Catmull-Rom through the hourly samples).
  const moonSamples = data.moon.r;
  const moonEci = (t) => {
    const x = (t - data.moon.t0) / data.moon.step;
    const i = Math.min(moonSamples.length - 2, Math.max(0, Math.floor(x)));
    const s = x - i;
    const p0 = moonSamples[Math.max(0, i - 1)], p1 = moonSamples[i], p2 = moonSamples[i + 1], p3 = moonSamples[Math.min(moonSamples.length - 1, i + 2)];
    return [0, 1, 2].map((k) => 0.5 * (2 * p1[k] + (-p0[k] + p2[k]) * s + (2 * p0[k] - 5 * p1[k] + 4 * p2[k] - p3[k]) * s * s +
      (-p0[k] + 3 * p1[k] - 3 * p2[k] + p3[k]) * s ** 3));
  };

  const earthPart = new Vector3();
  const moonPart = new Vector3();
  const moonInertial = new Vector3();

  /** Everything about the mission at `ms` (clamped to launch..splashdown). */
  const stateAt = (ms, out = {}) => {
    const t = Math.min(duration, Math.max(0, (ms - launchMs) / 1000));
    const clock = launchMs + t * 1000;
    const { r, v } = orion(t);
    const m = moonEci(t);
    const dE = Math.hypot(...r);
    const dMoonEarth = Math.hypot(...m);
    const rel = [r[0] - m[0], r[1] - m[1], r[2] - m[2]];
    const dM = Math.hypot(...rel);

    const gamma = Math.log(moonDistance / globeRadius) / Math.log(dMoonEarth / R_EARTH);
    sceneOf(r[0], r[1], r[2], earthPart).setLength(globeRadius * Math.max(1, dE / R_EARTH) ** gamma);
    sceneOf(m[0], m[1], m[2], moonInertial).normalize();
    sceneOf(rel[0], rel[1], rel[2], moonPart).multiplyScalar(moonRadius / R_MOON).addScaledVector(moonInertial, moonDistance);
    const w = smoothstep(MOON_FRAME_FROM, MOON_FRAME_TO, dM);
    out.inertial = (out.inertial || new Vector3()).lerpVectors(earthPart, moonPart, w);
    out.frameAngle = frameAngleAt(clock);
    out.position = (out.position || new Vector3()).copy(out.inertial).applyAxisAngle(Y_AXIS, out.frameAngle);
    out.moonDir = (out.moonDir || new Vector3()).copy(moonInertial).applyAxisAngle(Y_AXIS, out.frameAngle);

    out.t = t;
    out.ms = clock;
    out.altitudeKm = dE - R_EARTH;
    out.moonAltitudeKm = dM - R_MOON;
    out.speedKmS = Math.hypot(...v);
    out.phase = phaseAt(t);
    return out;
  };

  const phases = [
    [at("meco"), "Launch"],
    [at("heo"), "Climbing to orbit"],
    [at("tli"), "High Earth orbit"],
    [at("flyby") - 20 * 3600, "Outbound to the Moon"],
    [at("flyby") + 20 * 3600, "Around the Moon"],
    [at("entry"), "Homeward"],
    [at("splashdown") - 1, "Re-entry"],
    [Infinity, "Splashdown"],
  ];
  const phaseAt = (t) => phases.find(([end]) => t < end)[1];

  // Pacing: playback seconds → mission seconds. Slow for launch, the flyby
  // and re-entry; the multi-day coasts fly by.
  const H = 3600;
  const knots = [
    [0, 0],
    [5, at("meco")],
    [10, at("heo")],
    [17, at("tli")],
    [20, at("tli") + 4 * H],
    [28, at("flyby") - 10 * H],
    [31, at("flyby")],
    [35, at("flyby") + 8 * H],
    [44, at("entry") - 8 * H],
    [52, at("entry")],
    [60, at("splashdown")],
  ];
  const playbackSeconds = knots[knots.length - 1][0];
  const missionSecondsAt = monotoneCubic(knots.map((k) => k[0]), knots.map((k) => k[1]));
  const playbackAt = (t) => {
    let lo = 0, hi = playbackSeconds;
    for (let i = 0; i < 40; i++) {
      const mid = (lo + hi) / 2;
      if (missionSecondsAt(mid) < t) lo = mid; else hi = mid;
    }
    return (lo + hi) / 2;
  };

  return {
    launchMs,
    endMs: launchMs + duration * 1000,
    milestones: milestones.map((m) => ({ ...m, playback: playbackAt(m.t) })),
    playbackSeconds,
    /** Mission time (ms) at playback position u (seconds). */
    timeAt: (u) => launchMs + missionSecondsAt(u) * 1000,
    stateAt,
  };
}

/** Elapsed mission time as "T+ 3d 04:12:09". */
export function formatMet(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86400);
  const hh = String(Math.floor((s % 86400) / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `T+ ${d ? `${d}d ` : ""}${hh}:${mm}:${ss}`;
}
